import type { ReactNode } from 'react'
import clsx from 'clsx'
import { AlertTriangle, Clock, Power, Zap } from 'lucide-react'
import { TONE_BG, TONE_BORDER, TONE_TEXT } from './tokens'

export type VehicleStatus = 'moving' | 'idle' | 'loading' | 'alert' | 'offline'
export type ExceptionIcon = 'clock' | 'zap' | 'power' | 'alert'
export type ExceptionTone = 'warn' | 'danger' | 'info'

export interface VehicleException {
  icon?: ExceptionIcon
  text: string
  tone: ExceptionTone
}

export interface VehicleCardProps {
  plate: string
  status: VehicleStatus
  statusLabel: string
  place: string
  /** Live battery state of charge, 0-100. Rendered beside the plate, colour-coded by the
   *  same thresholds the MBMT right-hand panel's SocBar uses (<20 danger / <40 warn / else
   *  ok). Omitted entirely when null/undefined, so every caller that doesn't pass it
   *  renders exactly as before -- same convention `extra` below documents. */
  soc?: number | null
  /** Corridor load: Loaded (Dhar→Dhule) or Unloaded (Dhule→Dhar). */
  load?: 'Loaded' | 'Unloaded' | null
  /** TIMS / roster driver name. */
  driver?: string | null
  /** Lorry receipt from TIMS. Labelled LR on the card. */
  lr?: string | null
  trailer?: string | null
  /** Current TIMS event / phase, e.g. DHAR IN. */
  event?: string | null
  speed?: number | null
  progressPct?: number | null
  elapsed?: string
  exception?: VehicleException | null
  selected?: boolean
  onClick?: () => void
  className?: string
  /** Site-specific info the driver/lr/trailer/event slots above don't have a correct
   *  label for (their text is hardcoded to the TIMS/truck domain -- e.g. `lr`'s "LR"
   *  prefix would mislabel a bus schedule code). Rendered as its own row at the bottom
   *  of the card; omitted entirely when not passed, so every other caller is unaffected. */
  extra?: ReactNode
}

const STATUS_TEXT: Record<VehicleStatus, string> = {
  moving: 'text-[#16A34A]',
  idle: 'text-[#D97706]',
  loading: 'text-[#2563EB]',
  alert: 'text-[#DC2626]',
  offline: 'text-[#94A3B8]',
}

const STATUS_BG: Record<VehicleStatus, string> = {
  moving: 'bg-[#16A34A]',
  idle: 'bg-[#D97706]',
  loading: 'bg-[#2563EB]',
  alert: 'bg-[#DC2626]',
  offline: 'bg-[#94A3B8]',
}

const EXCEPTION_ICON: Record<ExceptionIcon, typeof Clock> = {
  clock: Clock,
  zap: Zap,
  power: Power,
  alert: AlertTriangle,
}

export function VehicleCard({
  plate,
  status,
  statusLabel,
  place,
  soc,
  load,
  driver,
  lr,
  trailer,
  event,
  speed,
  progressPct,
  elapsed,
  exception,
  selected,
  onClick,
  className,
  extra,
}: VehicleCardProps) {
  const Icon = EXCEPTION_ICON[exception?.icon ?? 'alert']
  const pct = progressPct != null ? Math.max(0, Math.min(100, progressPct)) : null
  const hasTrip = !!(driver || lr || trailer || event)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'block w-full rounded-[12px] border p-3 text-left transition-all',
        'hover:-translate-y-px hover:shadow-[0_2px_6px_rgba(15,23,42,0.08)] motion-reduce:hover:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
        exception
          ? [TONE_BG[exception.tone], TONE_BORDER[exception.tone]]
          : 'border-[rgba(15,23,42,0.07)] bg-white hover:border-[rgba(15,23,42,0.16)]',
        selected && 'ring-2 ring-[#2563EB]',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span className={clsx('h-2 w-2 shrink-0 rounded-full', STATUS_BG[status])} />
        <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{plate}</span>
        {soc != null ? (
          <>
            <span className="text-[#94A3B8]">·</span>
            <span className={clsx(
              'font-mono text-[12px] font-semibold tabular-nums',
              soc < 20 ? 'text-[#DC2626]' : soc < 40 ? 'text-[#D97706]' : 'text-[#16A34A]',
            )}>
              {Math.round(soc)}%
            </span>
          </>
        ) : null}
        {load ? (
          <span
            className={clsx(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              load === 'Loaded'
                ? 'bg-[#FEF3C7] text-[#92400E]'
                : 'bg-[#F1F5F9] text-[#475569]',
            )}
            title={load === 'Loaded' ? 'Dhar → Dhule · loaded' : 'Dhule → Dhar · unloaded'}
          >
            {load}
          </span>
        ) : null}
        <span className="ml-auto font-mono text-[12px] tabular-nums text-[#0F172A]">
          {speed != null ? (
            <>
              {speed} <span className="text-[#94A3B8]">KMPH</span>
            </>
          ) : (
            <span className="text-[#94A3B8]">—</span>
          )}
        </span>
      </div>

      <div className="mt-1 flex items-baseline gap-1.5 pl-4 text-[13px] leading-snug">
        <span className={clsx('font-medium', STATUS_TEXT[status])}>{statusLabel}</span>
        <span className="text-[#94A3B8]">·</span>
        <span className="truncate text-[#64748B]">{place}</span>
      </div>

      {hasTrip ? (
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 pl-4 text-[11px] leading-snug text-[#64748B]">
          {driver ? (
            <span className="truncate font-medium text-[#334155]" title="Driver">
              {driver}
            </span>
          ) : null}
          {lr ? (
            <>
              {driver ? <span className="text-[#CBD5E1]">·</span> : null}
              <span className="shrink-0 font-mono tabular-nums text-[#334155]">
                <span className="font-semibold tracking-wide text-[#475569]">LR</span>
                {' '}{lr}
              </span>
            </>
          ) : null}
          {trailer ? (
            <>
              {(driver || lr) ? <span className="text-[#CBD5E1]">·</span> : null}
              <span className="truncate">{trailer}</span>
            </>
          ) : null}
          {event ? (
            <span
              className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[#EEF2FF] text-[#3730A3]"
              title="TIMS event"
            >
              {event}
            </span>
          ) : null}
        </div>
      ) : null}

      {pct != null || elapsed ? (
        <div className="mt-2.5 flex items-center gap-2 pl-4">
          {pct != null ? (
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-[rgba(15,23,42,0.07)]">
              <div className={clsx('h-full rounded-full transition-[width] duration-500 ease-out', STATUS_BG[status])} style={{ width: `${pct}%` }} />
            </div>
          ) : null}
          {elapsed ? (
            <span className={clsx('font-mono text-[11px] tabular-nums text-[#94A3B8]', pct == null && 'ml-auto')}>
              {elapsed}
            </span>
          ) : null}
        </div>
      ) : null}

      {exception ? (
        <div
          className={clsx(
            // Card body carries the same tint; the tinted border is what delineates the chip.
            'mt-2.5 flex w-full items-center gap-1.5 rounded-[8px] border px-2 py-1.5',
            TONE_BG[exception.tone],
            TONE_BORDER[exception.tone],
          )}
        >
          <Icon className={clsx('h-3 w-3 shrink-0', TONE_TEXT[exception.tone])} />
          <span className={clsx('text-[12px] font-medium leading-tight', TONE_TEXT[exception.tone])}>
            {exception.text}
          </span>
        </div>
      ) : null}

      {extra}
    </button>
  )
}
